<!--
  @component ConnectionCard — "how your deployment reaches this resource."
  tier:      primitive (visual atom)
  purpose:   the tucked-away technical detail for a provisioned resource
             (database, storage, cache). Shows connection fields; secret
             values are masked with a reveal-guard + copy. Non-technical
             users can ignore it; pros can grab the connection string.
-->
<script lang="ts" module>
  export interface ConnectionField {
    label: string;
    value: string;
    /** Mask the value behind a reveal toggle. */
    secret?: boolean;
    /** Render value in monospace. Default true. */
    mono?: boolean;
  }
</script>

<script lang="ts">
  interface Props {
    title?: string;
    fields: ConnectionField[];
  }
  let { title = "Connection", fields }: Props = $props();

  let revealed = $state<Record<number, boolean>>({});
  let copied = $state<number | null>(null);

  function toggle(i: number): void {
    revealed = { ...revealed, [i]: !revealed[i] };
  }
  async function copy(i: number, value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      copied = i;
      setTimeout(() => { if (copied === i) copied = null; }, 1500);
    } catch { /* clipboard unavailable */ }
  }
  function masked(v: string): string {
    return v.length <= 6 ? "••••••" : `${v.slice(0, 3)}${"•".repeat(Math.min(18, v.length - 3))}`;
  }
</script>

<div class="conn">
  <p class="conn-title">{title}</p>
  <dl class="fields">
    {#each fields as f, i (f.label)}
      <div class="field">
        <dt>{f.label}</dt>
        <dd>
          <span class="value" class:mono={f.mono !== false}>
            {f.secret && !revealed[i] ? masked(f.value) : f.value}
          </span>
          <span class="field-actions">
            {#if f.secret}
              <button type="button" class="icon-btn" onclick={() => toggle(i)} aria-label={revealed[i] ? "Hide" : "Reveal"}>
                {revealed[i] ? "Hide" : "Reveal"}
              </button>
            {/if}
            <button type="button" class="icon-btn" onclick={() => void copy(i, f.value)} aria-label="Copy">
              {copied === i ? "Copied" : "Copy"}
            </button>
          </span>
        </dd>
      </div>
    {/each}
  </dl>
</div>

<style>
  .conn {
    border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background: var(--surface-sunken);
    padding: var(--space-3) var(--space-4);
    font-family: var(--type-font);
  }
  .conn-title {
    margin: 0 0 var(--space-2);
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }
  .fields { margin: 0; display: flex; flex-direction: column; gap: var(--space-1); }
  .field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: 6px 0;
  }
  .field + .field { border-top: 0.5px solid var(--border-subtle); }
  dt { font-size: 12px; color: var(--text-tertiary); flex-shrink: 0; }
  dd {
    margin: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
    flex: 1;
    justify-content: flex-end;
  }
  .value {
    font-size: 12.5px;
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .value.mono { font-family: var(--type-mono); font-size: 12px; }
  .field-actions { display: flex; gap: var(--space-1); flex-shrink: 0; }
  .icon-btn {
    appearance: none;
    background: transparent;
    border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    font: inherit;
    font-size: 11px;
    padding: 2px var(--space-2);
    cursor: pointer;
    transition: color var(--ease-fast), border-color var(--ease-fast);
  }
  .icon-btn:hover { color: var(--text-primary); border-color: var(--border); }
</style>
